// Shared look of the trading chat (from the "Trading Assistant Chat" design):
// monospace, black 1px borders, grey window headers, pink symbol chips.

import React, { CSSProperties, ReactNode, RefObject, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ContractInfo, OrderStatus, QuoteRow } from "./types";

export const C = {
  border: "#000",
  divider: "#D4D4D4",
  headerBg: "#F2F2F2",
  muted: "#6B7280",
  text: "#111",
  chip: "#F8CFCF",
  buy: "#2B72CB",
  sell: "#C2410C",
  live: "#1E9E5A",
  stale: "#E0A800",
  cancelled: "#D32F2F",
  done: "#555555",
  disabled: "#999999",
  selected: "#FBEE8A",
  gain: "#1D5FB5",
  loss: "#B8470B",
};

export const FONT = "'IBM Plex Mono', Menlo, Consolas, monospace";

// ---- formatting ------------------------------------------------------------------

export const fmt = (v: number | null | undefined, digits = 2) => (v == null ? "—" : v.toFixed(digits));

// money totals, Python-style digit grouping from 1000 on: 11110 -> "11_110.00"
export function fmtTotal(v: number, decimals = 2) {
  const [int, dec] = Math.abs(v).toFixed(decimals).split(".");
  return `${v < 0 ? "−" : ""}${int.replace(/\B(?=(\d{3})+(?!\d))/g, "_")}${dec ? `.${dec}` : ""}`;
}

const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

export function daysTo(expiry: string) {
  const [y, m, d] = expiry.split("-").map(Number);
  const today = new Date();
  const start = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((Date.UTC(y, m - 1, d) - start) / 86400000);
}

// "20 NOV 26 (24d)"
export function expiryLabel(expiry: string) {
  const [y, m, d] = expiry.split("-");
  return `${d} ${MONTHS[Number(m) - 1]} ${y.slice(2)} (${daysTo(expiry)}d)`;
}

// "20 NOV 26 3.00 C (24d)"; empty for stocks
export function optionLine(c: ContractInfo) {
  if (c.secType !== "OPT" || !c.expiry) return "";
  const [y, m, d] = c.expiry.split("-");
  return `${d} ${MONTHS[Number(m) - 1]} ${y.slice(2)} ${fmt(c.strike)} ${c.right} (${daysTo(c.expiry)}d)`;
}

// IB's long names are upper case ("JETBLUE AIRWAYS CORP")
export function companyName(c: ContractInfo | undefined) {
  if (!c?.name) return "";
  return c.name.toLowerCase().replace(/\b[a-z]/g, (ch) => ch.toUpperCase());
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// "2026-10-27 16:40:05 (Tue)"
export function stamp(ts: number) {
  const d = new Date(ts);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())} (${WEEKDAYS[d.getDay()]})`;
}

// message header of a list opened with the footer buttons (timestamp + live intro)
export function IntroHeader({ stampLine, text }: { stampLine?: string; text: string }) {
  if (!stampLine) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <span style={{ color: C.muted }}>{stampLine}</span>
      <span style={{ fontSize: 13 }}>{text}</span>
    </div>
  );
}

// ---- order status ------------------------------------------------------------------

export type WindowPhase = "preview" | OrderStatus;

export const PHASE_DOT: Record<WindowPhase, string> = {
  preview: C.buy,
  pending: C.stale,
  submitted: C.stale,
  partial: C.stale,
  filled: C.live,
  cancelled: C.cancelled,
  rejected: C.cancelled,
};

export const PHASE_LABEL: Record<WindowPhase, string> = {
  preview: "preview",
  pending: "submitted",
  submitted: "submitted",
  partial: "partially filled",
  filled: "filled",
  cancelled: "cancelled",
  rejected: "rejected",
};

export const isWorking = (p: WindowPhase) => p === "pending" || p === "submitted" || p === "partial";
export const isDone = (p: WindowPhase) => p === "filled" || p === "cancelled" || p === "rejected";

// true while the element is narrower than `width` (phones): the column lists
// switch to two-line rows
export const NARROW_WIDTH = 440;
export function useNarrow(ref: RefObject<HTMLElement | null>, width = NARROW_WIDTH) {
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => setNarrow(entry.contentRect.width < width));
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, width]);
  return narrow;
}

// "LONG MDB" tag + chip; options: "30OCT26 420.00 C" next to it (inline) or below
export function SymbolCell({ tag, contract, inline = false }: { tag: ReactNode; contract: ContractInfo; inline?: boolean }) {
  const optionLine = contract.secType === "OPT" ? contract.label.slice(contract.symbol.length + 1) : null;
  return (
    <span style={{ display: "flex", flexDirection: inline ? "row" : "column", alignItems: inline ? "center" : undefined, gap: inline ? 6 : 0, minWidth: 0, overflow: inline ? "hidden" : undefined }}>
      <span style={{ flex: "none", display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
        {tag}
        <Chip style={{ fontSize: 13 }}>{contract.symbol}</Chip>
      </span>
      {optionLine && (
        <span style={{ color: C.muted, fontSize: 11, whiteSpace: "nowrap", ...(inline ? { overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 } : {}) }}>{optionLine}</span>
      )}
    </span>
  );
}

// ---- icons ------------------------------------------------------------------------

const ICONS = {
  ladder: { d: "M13 3L5 14h6l-1 7 8-11h-6z", w: 1.8 },
  plus: { d: "M12 5v14M5 12h14", w: 2 },
  close: { d: "M6 6l12 12M18 6L6 18", w: 2 },
  pencil: { d: "M4 20h4L19 9l-4-4L4 16v4zM13 7l4 4", w: 1.8 },
  reverse: { d: "M7 4v13M3 13l4 4 4-4M17 20V7M13 11l4-4 4 4", w: 1.8 },
};

export function Icon({ name, size = 16 }: { name: keyof typeof ICONS; size?: number }) {
  const icon = ICONS[name];
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={icon.w}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={icon.d} />
    </svg>
  );
}

// ---- building blocks -------------------------------------------------------------

export function Chip({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return <span style={{ background: C.chip, padding: "0 5px", fontWeight: 600, ...style }}>{children}</span>;
}

export function SideTag({ side, label }: { side: "BUY" | "SELL"; label?: string }) {
  return (
    <span style={{ background: side === "BUY" ? C.buy : C.sell, color: "#fff", fontSize: 11, fontWeight: 600, padding: "1px 7px" }}>
      {label ?? side}
    </span>
  );
}

export function StatusDot({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: "flex", alignItems: "center", gap: 6, color: C.text }}>
      <span style={{ width: 9, height: 9, borderRadius: "50%", background: color }} />
      {label}
    </span>
  );
}

export function freshnessDot(freshness: "live" | "stale" | "loading", dataType?: string) {
  if (freshness === "loading") return <StatusDot color={C.disabled} label="loading" />;
  if (freshness === "stale") return <StatusDot color={C.stale} label="stale" />;
  if (dataType && dataType !== "live") return <StatusDot color={C.stale} label={dataType} />;
  return <StatusDot color={C.live} label="live" />;
}

export const iconButton = (size: number, extra?: CSSProperties): CSSProperties => ({
  flex: "none",
  width: size,
  height: size,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#fff",
  color: C.text,
  border: `1px solid ${C.border}`,
  padding: 0,
  cursor: "pointer",
  ...extra,
});

export function IconButton(props: { icon: keyof typeof ICONS; label: string; title?: string; onClick?: () => void; size?: number; iconSize?: number; style?: CSSProperties; disabled?: boolean }) {
  const { icon, label, title, onClick, size = 36, iconSize = 16, style, disabled } = props;
  return (
    <button aria-label={label} title={title ?? label} onClick={onClick} disabled={disabled} style={iconButton(size, style)}>
      <Icon name={icon} size={iconSize} />
    </button>
  );
}

// a window in the feed: grey header (title left, status + duplicate/close right) and body
export function WidgetBox(props: {
  title: ReactNode;
  status?: ReactNode;
  onDuplicate?: () => void;
  onClose?: () => void;
  children: ReactNode;
  boxRef?: React.Ref<HTMLDivElement>;
  headerAlign?: "center" | "flex-start";
}) {
  const { title, status, onDuplicate, onClose, children, boxRef, headerAlign = "flex-start" } = props;
  return (
    <div ref={boxRef} style={{ border: `1px solid ${C.border}`, background: "#fff" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: headerAlign, gap: 8, padding: "6px 10px", background: C.headerBg, borderBottom: `1px solid ${C.border}` }}>
        <div style={{ minWidth: 0 }}>{title}</div>
        <div style={{ flex: "none", display: "flex", alignItems: "center", gap: 8 }}>
          {status}
          {onDuplicate && <IconButton icon="plus" label="Duplicate this window" title="Duplicate" onClick={onDuplicate} size={24} iconSize={12} />}
          {onClose && <IconButton icon="close" label="Close this window" title="Close" onClick={onClose} size={24} iconSize={12} />}
        </div>
      </div>
      {children}
    </div>
  );
}

// header title of an order window: [BUY] [JBLU] JetBlue Airways Corp. / option line
export function ContractTitle({ contract, side }: { contract?: ContractInfo; side?: "BUY" | "SELL" }) {
  const line = contract ? optionLine(contract) : "";
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <span>
        {side && <><SideTag side={side} /> </>}
        <Chip>{contract?.symbol ?? "…"}</Chip> {companyName(contract)}
      </span>
      {line && <span style={{ color: C.muted }}>{line}</span>}
    </div>
  );
}

// an option window's underlying: "APLD 21.30 | 21.32 | 21.35" (bid | last | ask)
export function UnderlyingQuote({ row }: { row?: QuoteRow }) {
  if (!row || !("quote" in row)) return <span />;
  const q = row.quote;
  const sep = <span style={{ color: C.muted }}> | </span>;
  return (
    <span style={{ fontSize: 12.5, whiteSpace: "nowrap" }} title={`${row.contract.symbol} bid | last | ask`}>
      <span style={{ fontWeight: 600 }}>{row.contract.symbol}</span> {fmt(q.bid)}{sep}{fmt(q.last)}{sep}{fmt(q.ask)}
    </span>
  );
}

// a list of values to pick from, opened by clicking a SizeField / PriceField: the values in
// rows, the largest on top, each with an optional note in parentheses
interface Picker {
  values: number[];
  current: number;
  note?: (v: number) => string | null;
  format?: (v: number) => string;
  onPick: (v: number) => void;
}

const PICKER_ROW = 30;
const PICKER_ROWS = 9;
// the sizes of a SizeField's list (plus the current one), from 1 share / contract up
const SIZE_CHOICES = [1, 2, 3, 4, 5, 10, 15, 20, 25, 30, 40, 50, 75, 100, 150, 200, 250, 300, 400, 500, 750,
  1000, 1500, 2000, 2500, 3000, 4000, 5000, 7500, 10000];
// the steps (in cents) of a PriceField's list away from the current price:
// 10 prices each, so 50 above and 50 below (down to 0.01)
const PRICE_LIST_STEPS = [1, 5, 10, 20, 50];
const PRICE_LIST_PER_STEP = 10;

// a PriceField's list, largest on top: the current price, then from it outwards
// in PRICE_LIST_STEPS, each price on its step's grid (8.19 -> 8.20 … 8.29, 8.30, 8.35 …)
function priceChoices(price: number): number[] {
  const cents = Math.round(price * 100);
  const above: number[] = [];
  const below: number[] = [];
  let up = cents;
  let down = cents;
  for (const step of PRICE_LIST_STEPS) {
    for (let i = 0; i < PRICE_LIST_PER_STEP; i++) {
      up = (Math.floor(up / step) + 1) * step;
      above.push(up / 100);
      down = (Math.ceil(down / step) - 1) * step;
      if (down >= 1) below.push(down / 100);
    }
  }
  return [...above.reverse(), price, ...below];
}

// over everything (a portal with fixed position: the chat feed scrolls and
// would cut it off), centered on the value it opened from; closes on a pick,
// a click outside, Escape or a scroll / resize
function PickerList({ anchor, picker, onClose }: { anchor: HTMLElement; picker: Picker; onClose: () => void }) {
  const listRef = useRef<HTMLDivElement>(null);
  const rect = anchor.getBoundingClientRect();
  const height = Math.min(picker.values.length, PICKER_ROWS) * PICKER_ROW + 2;
  const width = Math.max(rect.width, 180);
  const top = Math.min(Math.max(4, rect.top + rect.height / 2 - height / 2), window.innerHeight - height - 4);
  const left = Math.min(Math.max(4, rect.left + rect.width / 2 - width / 2), window.innerWidth - width - 4);

  useLayoutEffect(() => {
    // once on open: the current value in the middle of the list (the windows
    // re-render with every quote poll, which must not move the list)
    const i = picker.values.indexOf(picker.current);
    if (listRef.current && i >= 0) listRef.current.scrollTop = i * PICKER_ROW - (height - PICKER_ROW) / 2;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // the latest onClose without re-subscribing the listeners on every render
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const onClose = () => close.current();
    const outside = (e: Event) => {
      if (!listRef.current?.contains(e.target as Node) && !anchor.contains(e.target as Node)) onClose();
    };
    const key = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    const scroll = (e: Event) => !listRef.current?.contains(e.target as Node) && onClose();
    document.addEventListener("pointerdown", outside, true);
    document.addEventListener("keydown", key);
    window.addEventListener("scroll", scroll, true);
    window.addEventListener("resize", onClose);
    return () => {
      document.removeEventListener("pointerdown", outside, true);
      document.removeEventListener("keydown", key);
      window.removeEventListener("scroll", scroll, true);
      window.removeEventListener("resize", onClose);
    };
  }, [anchor]);

  return createPortal(
    // overscroll contained: scrolling past the list's ends doesn't scroll (and close it via) the chat behind
    <div ref={listRef} role="listbox" style={{ position: "fixed", top, left, width, height, overflowY: "auto", overscrollBehavior: "contain", boxSizing: "border-box", background: "#fff", border: `1px solid ${C.border}`, boxShadow: "0 4px 16px rgba(0, 0, 0, 0.18)", zIndex: 10000, fontFamily: FONT }}>
      {picker.values.map((v) => {
        const note = picker.note?.(v);
        const on = v === picker.current;
        return (
          <div key={v} role="option" aria-selected={on} onClick={() => { picker.onPick(v); onClose(); }}
            style={{ height: PICKER_ROW, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, cursor: "pointer", fontSize: 14, fontWeight: on ? 600 : 400, background: on ? C.selected : "#fff", borderBottom: `1px solid ${C.divider}`, boxSizing: "border-box" }}>
            <span>{picker.format ? picker.format(v) : v}</span>
            {note && <span style={{ color: C.muted }}>({note})</span>}
          </div>
        );
      })}
    </div>,
    document.body,
  );
}

// the size / price as text typed via the keyboard: whole shares / contracts, cents
export const parseSize = (text: string): number | null => {
  const v = Math.round(Number(text.trim().replace(",", ".")));
  return Number.isFinite(v) && v >= 1 ? v : null;
};
export const parsePrice = (text: string): number | null => {
  const v = Math.round(Number(text.trim().replace(",", ".")) * 100) / 100;
  return Number.isFinite(v) && v >= 0.01 ? v : null;
};

// the pencil at the left of a value box: switches it to a free text input
const EDIT_BUTTON_WIDTH = 26;
function EditButton({ what, onClick, disabled }: { what: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button aria-label={`Edit ${what}`} title={`Edit ${what}`} disabled={disabled}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      style={iconButton(EDIT_BUTTON_WIDTH, { position: "absolute", left: 0, top: 0, height: "100%", border: 0, borderRight: `1px solid ${C.border}`, background: "transparent", cursor: disabled ? undefined : "pointer" })}>
      <Icon name="pencil" size={14} />
    </button>
  );
}

// the value box being edited: free text, parsed (and sent to onCommit) only
// once the focus is lost or on Enter; Escape or text that doesn't parse keeps the old value.
// The blur happens on the mousedown of a Buy / Sell / Submit button, before its click.
function EditInput({ initial, what, parse, onCommit, onDone, fontSize }: { initial: string; what: string; parse: (text: string) => number | null; onCommit: (v: number) => void; onDone: () => void; fontSize: number }) {
  const [text, setText] = useState(initial);
  const cancelled = useRef(false);
  return (
    <input aria-label={what} autoFocus value={text} inputMode="decimal"
      onFocus={(e) => e.target.select()}
      onChange={(e) => setText(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") { cancelled.current = true; e.currentTarget.blur(); }
      }}
      onBlur={() => {
        const v = parse(text);
        if (!cancelled.current && v != null) onCommit(v);
        onDone();
      }}
      style={{ width: "100%", height: "100%", boxSizing: "border-box", border: 0, outline: "none", padding: 0, background: "transparent", textAlign: "center", fontFamily: "inherit", fontSize, fontWeight: 600, color: C.text }} />
  );
}

// a value of an order window: clicking it opens the list of `values` to pick
// from, each with its estimated total (`note`); the pencil at its left lets
// the value be typed instead (parsed with `parse` once the focus is lost)
function ListField(props: { what: string; value: number; values: number[]; format: (v: number) => string; parse: (text: string) => number | null; onChange: (v: number) => void; note?: (v: number) => string | null; disabled?: boolean; big?: boolean }) {
  const { what, value, values, format, parse, onChange, note, disabled, big = true } = props;
  const valueRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);
  const fontSize = big ? 18 : 16;
  return (
    <>
      <div ref={valueRef} aria-label={editing ? undefined : what} aria-haspopup="listbox" aria-expanded={open} onClick={() => !disabled && !editing && setOpen(!open)}
        style={{ position: "relative", flex: 1, minWidth: 0, height: big ? 44 : 36, boxSizing: "border-box", display: "flex", alignItems: "center", justifyContent: "center", paddingLeft: EDIT_BUTTON_WIDTH, background: "#fff", border: `1px solid ${open || editing ? C.buy : C.border}`, fontSize, fontWeight: 600, cursor: disabled || editing ? undefined : "pointer" }}>
        <EditButton what={what} disabled={disabled || editing} onClick={() => { setOpen(false); setEditing(true); }} />
        {editing && !disabled
          ? <EditInput initial={format(value)} what={what} parse={parse} onCommit={onChange} onDone={() => setEditing(false)} fontSize={fontSize} />
          : format(value)}
      </div>
      {open && valueRef.current && <PickerList anchor={valueRef.current} picker={{ values, current: value, note, format, onPick: onChange }} onClose={() => setOpen(false)} />}
    </>
  );
}

// the size of an order window: the list holds SIZE_CHOICES and the current size
export function SizeField(props: { size: number; onChange: (v: number) => void; note?: (v: number) => string | null; disabled?: boolean; big?: boolean }) {
  const { size, ...rest } = props;
  const values = SIZE_CHOICES.includes(size) ? [...SIZE_CHOICES] : [...SIZE_CHOICES, size].sort((x, y) => x - y);
  values.reverse();
  return <ListField what="size" value={size} values={values} format={String} parse={parseSize} {...rest} />;
}

// the limit price of an order window: the list is centered on the current price (priceChoices)
export function PriceField(props: { price: number; onChange: (v: number) => void; note?: (v: number) => string | null; disabled?: boolean; big?: boolean }) {
  const { price, ...rest } = props;
  return <ListField what="price" value={price} values={priceChoices(price)} format={(v) => fmt(v)} parse={parsePrice} {...rest} />;
}

export function StepChoice<T extends number>(props: { options: T[]; value: T; onChange: (v: T) => void; disabled?: boolean }) {
  return (
    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
      {props.options.map((v) => (
        <button key={v} onClick={() => props.onChange(v)} disabled={props.disabled}
          style={{ height: 28, minWidth: 36, padding: "0 8px", background: v === props.value ? C.selected : "#fff", color: C.text, border: `1px solid ${C.border}`, fontSize: 12, fontWeight: v === props.value ? 600 : 400 }}>
          {v}
        </button>
      ))}
    </span>
  );
}
